import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Table, Tag, Segmented } from 'antd';
import { apiService } from '../../../services/apiService';

const ShopifyDashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [rangeDays, setRangeDays] = useState<number>(7);

  useEffect(() => {
    const ctrl = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        // 取 2 倍天數，供本期 vs 上期對比
        const resp: any = await apiService.getShopifyDashboard({ days: rangeDays * 2 });
        setStats(resp?.data || resp);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => ctrl.abort();
  }, [rangeDays]);

  // 原始日彙總
  const allRows = (stats?.byDay || []).map((d: any) => ({ key: d.day, day: d.day, orders: d.orders, sales: Number(d.sales || 0) }));
  const rows = allRows.slice(-rangeDays);
  const currency = stats?.currency || '$';

  const fulfillmentData = useMemo(()=> (stats?.fulfillment || []).map((r:any)=>({ key:r.status, status:r.status, count:r.count })), [stats]);
  const financialData = useMemo(()=> (stats?.financial || []).map((r:any)=>({ key:r.status, status:r.status, count:r.count })), [stats]);
  const topProducts = useMemo(()=> (stats?.topProducts || []).map((r:any)=>({ key:r.key, name:r.key, qty:r.qty, revenue:Number(r.revenue||0) })), [stats]);
  const recentOrders = useMemo(()=> (stats?.recentOrders || []).map((r:any)=>({ key:r.id, name:r.name, createdAt:r.created_at, total:Number(r.total_price||0), fulfillment:r.fulfillment_status||'unfulfilled' })), [stats]);

  // 產生簡易折線圖（純 CSS），參考 HKTVmall 模擬
  const linePoints = rows.map((r, idx) => ({ x: idx, y: r.sales }));
  const maxY = Math.max(...linePoints.map(p => p.y), 1);
  const poly = linePoints.length > 1
    ? linePoints.map((p, i) => `${(i/(Math.max(linePoints.length-1,1)))*100},${100 - (p.y/maxY)*100}`).join(' ')
    : `0,100 100,100`;

  // 本週 vs 上週（以最近 14 天比較，展示訂單數）
  const last14 = allRows.slice(-14);
  const last7 = last14.slice(-7);
  const prev7 = last14.slice(0, Math.max(last14.length-7,0));
  const weekMax = Math.max(...[...last7, ...prev7].map(r=>r?.orders||0), 1);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Row gutter={16}>
        <Col xs={12} md={6}><Card loading={loading}><Statistic title="Orders" value={stats?.orders || 0} /></Card></Col>
        <Col xs={12} md={6}><Card loading={loading}><Statistic title="Products" value={stats?.products || 0} /></Card></Col>
        <Col xs={12} md={6}><Card loading={loading}><Statistic title="Unfulfilled" value={stats?.unfulfilled || 0} /></Card></Col>
        <Col xs={12} md={6}>
          <Card loading={loading}>
            {(() => {
              const cur = rows.reduce((s,r)=>s+(r.sales||0),0);
              const prev = allRows.slice(-(rangeDays*2), -rangeDays).reduce((s,r)=>s+(r.sales||0),0);
              const diff = prev ? ((cur - prev) / prev) * 100 : 0;
              const up = diff >= 0;
              return (
                <Statistic
                  title="Total Sales"
                  prefix={currency==='USD'?'$':currency}
                  value={cur || stats?.totalSales || 0}
                  precision={2}
                  suffix={<span style={{ color: up ? '#52c41a' : '#f5222d', fontSize: 12, marginLeft: 8 }}>{up ? '↑' : '↓'} {Math.abs(diff).toFixed(1)}%</span>}
                />
              );
            })()}
          </Card>
        </Col>
      </Row>

      <Card 
        title={
          <Space>
            Daily Orders & Sales
            <Segmented size="small" options={[{label:'7d', value:7},{label:'14d', value:14},{label:'30d', value:30}]} value={rangeDays} onChange={(v)=> setRangeDays(Number(v))} />
          </Space>
        }
        loading={loading}
      >
        <div style={{ display:'flex', gap:16, alignItems:'stretch', flexWrap:'wrap' }}>
          <div style={{ flex:'1 1 360px', minWidth:260, padding:'8px 8px 0', background:'linear-gradient(180deg,#f0f5ff,#fff)', borderRadius:8 }}>
            {/* 週對比條圖（只看最近 14 天） */}
            <div style={{ padding: '8px 8px 0' }}>
              <div style={{ display:'flex', gap: 8, alignItems:'flex-end', height: 120 }}>
                {Array.from({length:7}).map((_,i)=> (
                  <div key={i} style={{ flex:1, textAlign:'center' }}>
                    <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'center', gap:3, height: 100 }}>
                      <div title={`Last ${prev7[i]?.day||''}: ${prev7[i]?.orders||0}`} style={{ width: 8, height: `${Math.round(100*(prev7[i]?.orders||0)/weekMax)}%`, background:'#d3f261', borderRadius: 4 }} />
                      <div title={`This ${last7[i]?.day||''}: ${last7[i]?.orders||0}`} style={{ width: 8, height: `${Math.round(100*(last7[i]?.orders||0)/weekMax)}%`, background:'#69c0ff', borderRadius: 4 }} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{(last7[i]?.day||'').slice(5)}</Typography.Text>
                  </div>
                ))}
              </div>
              <Space size={12} style={{ marginTop: 6 }}>
                <Tag color="#d3f261">Last 7d</Tag>
                <Tag color="#69c0ff">This 7d</Tag>
              </Space>
            </div>
            {/* 折線圖 */}
            <div style={{ height: 140, position:'relative' }}>
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position:'absolute', inset:0 }}>
                <polyline fill="none" stroke="#69c0ff" strokeWidth="2" points={poly} />
              </svg>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', color:'#888', fontSize:12, padding:'0 4px 8px' }}>
              {rows.slice(0,7).map(r=> <span key={r.key}>{(r.day||'').slice(5)}</span>)}
            </div>
          </div>
          <div style={{ flex:'1 1 360px', minWidth:260 }}>
            <Table
              size="small"
              dataSource={rows}
              pagination={{ pageSize: 7 }}
              columns={[
                { title: 'Day', dataIndex: 'day', width: 140 },
                { title: 'Orders', dataIndex: 'orders', width: 120 },
                { title: 'Sales', dataIndex: 'sales', width: 160, render: (v:number)=> (currency==='USD'?'$':currency) + ' ' + v.toFixed(2) },
              ]}
            />
          </div>
        </div>
      </Card>

      {/* 移除狀態分佈卡片，保留更精簡的 Shopify 儀表板 */}

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card title="Top Products (by revenue)" loading={loading}>
            <Table size="small" dataSource={topProducts} pagination={{ pageSize: 5 }}
              columns={[
                { title: 'Product / SKU', dataIndex: 'name', render:(t:string, r:any)=> (
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {r.image_src ? <img src={r.image_src} alt="" width={36} height={36} style={{ objectFit:'cover', borderRadius:4 }} /> : <div style={{ width:36, height:36, background:'#f5f5f5', borderRadius:4 }} />}
                    <span>{t}</span>
                  </div>
                ) },
                { title: 'Qty', dataIndex: 'qty', width: 100 },
                { title: 'Revenue', dataIndex: 'revenue', width: 160, render:(v:number)=> (currency==='USD'?'$':currency) + ' ' + v.toFixed(2) },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Recent Orders" loading={loading}>
            <Table size="small" dataSource={recentOrders} pagination={false}
              columns={[
                { title: 'Order', dataIndex: 'name', width: 140 },
                { title: 'Date', dataIndex: 'createdAt', width: 180 },
                { title: 'Total', dataIndex: 'total', width: 140, render:(v:number)=> (currency==='USD'?'$':currency) + ' ' + v.toFixed(2) },
                { title: 'Fulfillment', dataIndex: 'fulfillment', render:(s:string)=>(<Tag color={s==='fulfilled'?'green':s==='partial'?'orange':'default'}>{s||'unfulfilled'}</Tag>) },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default ShopifyDashboard;


